/*************************************************************
 * Tracks and saves the progress of a scorm course
 * 
 * To view at runtime check console for:  window.scormTracker
 *************************************************************/

import { duration } from 'moment';
import { gql } from 'apollo-boost';
import { ScormRuntime12 } from './ScormRuntime12';  
import { ScormRuntime2004 } from './ScormRuntime2004'; 
import { apolloService as apolloClient } from '../services/apolloService';
import Bugsnag from '@bugsnag/js'; 
import { v4 as uuidv4 } from 'uuid';

const SAVE_INTERVAL = 2000;
const WINDOW_CLOSE_INTERVAL = 200;
const IN_PROGRESS = 'IN_PROGRESS', COMPLETED = 'COMPLETED', ASSIGNED = 'ASSIGNED';
const PATCH_ASSIGNMENT_MUTATION = gql`
    mutation patchAssignment($input: PatchAssignmentInput!) {
        result: patchAssignment(input: $input) {
            id
            status
            score
            scormState
            createdAt
            updatedAt
        }
    }
`;

/*************************************************************
 * Entry point for tracking a scorm course. This acts like a 
 * singleton so only one instance of trackScorm ever exists.
 *************************************************************/

 export const trackScorm = ( course, lesson, studentName, studentId, localeCode ) => {
    if ( !window.scormTracker ) {
        window.scormTracker = new ScormTracker( { debug: false, debugAsNewCourse: false } );
    }
    
    window.scormTracker.startTracking( course, lesson, studentName, studentId, localeCode );
};

export const openWindow = ( url, closeCallback ) => {
    if ( window.scormTracker ) {
        return window.scormTracker.openWindow( url, closeCallback );
    } else {
        console.error( 'Not tracking scorm' );
    }
};

/*************************************************************
 * The actual tracker
 *************************************************************/

class ScormTracker {
    debug = false;
    debugAsNewCourse = false;
    course = {};
    courseId = '';
    lessonId = '';
    lesson = {};
    studentId = '';
    studentName = '';
    saveCache = null;
    scormVersion = '';
    isTracking = false;
    saveTimer = undefined;
    windowCloseTimer = undefined;
    lessonStatus = '';
    courseWindow = undefined; 
    closeCallback = undefined;
    localeCode = '';
    uuid = uuidv4();

    constructor( { debug = false, debugAsNewCourse = false } ) {
        this.debug = debug;
        this.debugAsNewCourse = debugAsNewCourse;
    }

    /********************************
     * Starts tracking and sets all 
     * listeners and vars
     ********************************/

    startTracking( course, lesson, studentName, studentId, localeCode ) {
        
        /********************************
         * Reset vars so nothing remains
         * from previously tracked course 
         ********************************/

        if ( !course || !lesson ) { 
            return;
        }

        if ( this.isTracking ) {
            this.stopTracking();
        }

        if ( this.courseWindow ) {
            this.courseWindow.close();
            this.courseWindow = undefined;
        }

        if ( this.windowCloseTimer ) {
            clearInterval( this.windowCloseTimer );
            this.windowCloseTimer = undefined;
        }

        /********************************
         * Setup vars and start tracking  
         ********************************/

        this.debug && console.log( 'Starting tracking' );
        
        this.course = course;
        this.localeCode = localeCode;
        this.courseId = course.id || course.node?.id;
        this.lessonId = lesson.id || lesson.node?.id;
        this.studentId = studentId;
        this.studentName = studentName;
        this.changeCalled = false; 

        if ( this.debugAsNewCourse ) {
            this.lesson = { ...lesson, assignment: undefined };
        } else {
            this.lesson = { ...lesson };
            this.lessonStatus = lesson.assignment?.status || IN_PROGRESS;
        }
        
        if ( !this.lesson.assignment ) {
            this.setInitialAssignment();
        }

        this.addScormListeners();
        this.startSaveTimer();
        this.isTracking = true;
    }
    
    /********************************
     * Stops tracking and resets all 
     * listeners and vars
     ********************************/

    stopTracking() {
        this.debug && console.log( 'Stoping tracking' );
        
        this.stopSaveTimer();
        this.removeScormListeners();
        this.course = {};
        this.lesson = {};
        this.studentId = '';
        this.studentName = '';
        this.saveCache = null;
        this.scormVersion = '';
        this.isTracking = false;
        this.changeCalled = false;
    }

    /********************************
     * Bugsnag Handler 
     ********************************/

    bugsnagNotifyError( error ) {
        Bugsnag.notify( error,  ( event ) => {
            event.severity = 'warning';
            event.context = `Scorm Tracker - ${ this.uuid }`;
            
            const crs =  {
                'course': this.course.id,
                'learningObject': this.lesson.id,
                'learningObjectStatus': this.lessonStatus,
                'localeCode': this.localeCode
            };
            
            event.addMetadata( 'course', crs ); 
        } );
    } 
        
    /********************************
     * Handler for opening scorm in 
     * a new window
     ********************************/

    openWindow( url, closeCallback ) {
        this.debug && console.log( 'Opening course in new window' );
        
        this.closeCallback = closeCallback;

        this.courseWindow = window.open(
            url,
            'CourseWindow',
            [ 
                'resizable=1',
                `width=${ window.innerWidth }`,
                `height=${ window.innerHeight }`,
                'left=0',
                'top=0',
            ].join( ',' )
        );
        
        // Scorm window error
        if ( !this.courseWindow || this.courseWindow === null ) {
            this.bugsnagNotifyError( 'Failed to open course window at openWindow()' );
            return;
        }

        window.courseWindow = this.courseWindow;
        
        this.windowCloseTimer = setInterval( () => {
            if ( this.courseWindow?.closed ) {
                this.bugsnagNotifyError( 'Closed course window' );
            
                if ( this.closeCallback ) {
                    this.closeCallback();
                }
                clearInterval( this.windowCloseTimer );
                this.windowCloseTimer = undefined;
            }
        }, WINDOW_CLOSE_INTERVAL );

        return this.courseWindow;
    }

    /********************************
     * Save and save timer handling
     ********************************/
    
    startSaveTimer() {
        this.debug && console.log( 'Starting timer' );

        if ( this.saveTimer ) {
            this.stopSaveTimer();
        }

        this.saveTimer = setInterval( this.save.bind( this ), SAVE_INTERVAL );
    }

    stopSaveTimer() {
        this.debug && console.log( 'Stoping timer' );
    
        if ( this.saveTimer ) {
            clearInterval( this.saveTimer );
            this.saveTimer = undefined;
        }
    }

    async save() {
        if ( this.saveCache !== null && this.lessonStatus !== COMPLETED ) {
            const model = { ...this.saveCache.model };
            const status = this.saveCache.status;
            const timeSpent = this.getTimeSpentInSeconds( model );
            const score = this.getScore( model );

            this.saveCache = null;
            this.lessonStatus = status;
            
            this.debug && console.log( 'Saving Scorm progress with: ', model, status, timeSpent, score );
            //this.bugsnagNotifyError( 'Saving scorm status' );
                    
            try {
                await apolloClient.mutate( { 
                    mutation: PATCH_ASSIGNMENT_MUTATION,
                    variables: {
                        input: { 
                            learningObjectID: this.lessonId,
                            courseID: this.courseId,
                            scormState: model,
                            status: status,
                            score: status === COMPLETED ? score : undefined,
                            timeSpent: status === COMPLETED ? timeSpent : undefined
                        }
                    }
                } );
            }
            catch ( error ) {
                console.error( error ); 
                this.bugsnagNotifyError( 'Failed to patch assignment at save()' );
            }
        }
    }

    /********************************
     * Scorm listener handling
     ********************************/

    addScormListeners() {
        //this.debug && console.log( 'Adding listeners' );

        window.API_1484_11 = new ScormRuntime2004( { 
            CMIModel: JSON.parse( JSON.stringify( this.lesson?.assignment?.scormState || {} ) ), 
            debug: true,
            localeCode: this.localeCode,
            studentId: this.studentId,
            studentName: this.studentName,
            eventCallbacks: {
                initialize: () => {
                    this.bugsnagNotifyError( 'ScormRuntime2004 initialize called' );
                    this.scormVersion = '2004';
                },
                change: ( event, model ) => { 
                    if ( this.changeCalled === false ) {
                        this.changeCalled = true;
                        this.bugsnagNotifyError( 'ScormRuntime2004 change called' );
                    }
                    this.addToSaveCache( model );
                },
                commit: ( event, model ) => {  
                    this.addToSaveCache( model );
                }
            }
        } );
    
        window.API = new ScormRuntime12( { 
            CMIModel: JSON.parse( JSON.stringify( this.lesson?.assignment?.scormState || {} ) ), 
            debug: true,
            localeCode: this.localeCode,
            studentId: this.studentId,
            studentName: this.studentName,
            eventCallbacks: {
                initialize: () => {
                    this.bugsnagNotifyError( 'ScormRuntime12 initialize called' );
                    this.scormVersion = '1.2';
                },
                change: ( event, model ) => { 
                    if ( this.changeCalled === false ) {
                        this.changeCalled = true;
                        this.bugsnagNotifyError( 'ScormRuntime12 change called' );
                    }
                    this.addToSaveCache( model );
                },
                commit: ( event, model ) => {  
                    this.addToSaveCache( model );
                }
            }
        } );
    }
    
    removeScormListeners() {
        this.debug && console.log( 'Removing listeners' );
        
        delete window.API_1484_11;
        delete window.API;
    }
    
    /********************************
     * Utility functions
     ********************************/

    addToSaveCache( model ) {
        const status = this.getCourseStatus( model );

        this.saveCache = { model, status };
    }

    getCourseStatus( model ) {
        const statusForScorm2004 = model?.cmi?.completion_status;

        if ( statusForScorm2004 ) {
            if ( statusForScorm2004 === 'completed' ) { 
                return COMPLETED ;
            } else {
                if ( statusForScorm2004 === 'incomplete' || statusForScorm2004 === 'not attempted' ) {
                    return IN_PROGRESS;
                } else {
                    return ASSIGNED;
                }
            }
        } 

        const statusForScorm12 = model?.cmi?.core?.lesson_status;

        if ( statusForScorm12 ) {
            if ( statusForScorm12 === 'completed' || statusForScorm12 === 'passed' ) {
                return COMPLETED;
              } else {
                  if ( statusForScorm12 === 'failed' || statusForScorm12 === 'incomplete' ) { 
                      return IN_PROGRESS ;
                  } else {
                      return ASSIGNED;
                  }
              }
        }

        return IN_PROGRESS;
    }

    getTimeSpentInSeconds( model ) { 
        let sessionTime = model?.cmi?.core?.session_time;
        let totalTime = model?.cmi?.core?.total_time;
    
        if ( !sessionTime && !totalTime ) {
            sessionTime = model?.cmi?.session_time;
            totalTime = model?.cmi?.total_time;
        }
    
        if ( !sessionTime && !totalTime ) {
            return undefined;
        }

        if ( sessionTime.startsWith( 'P' ) ) {  
            const regex = /^P(?:\d+Y|Y)?(?:\d+M|M)?(?:\d+D|D)?(?:T(\d+H|H)?(\d+M|M)?(\d+(\.\d{1,2})?S|S)?)?$/;
        
            let matches;
        
            if ( ( matches = regex.exec( sessionTime ) ) !== null ) {
                let hours = 0, min = 0, sec = 0; 
            
                matches.forEach( ( match ) => {
                    if ( match ) {
                        // Set hours
                        if ( match.endsWith( 'H' ) ) {
                            hours = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( hours ) ) {
                                hours = 0;
                            }
                        }
                    
                        // Set minuetes
                        if ( match.endsWith( 'M' ) ) { 
                            min = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( min ) ) {
                                min = 0;
                            }
                        }
                    
                            // Set seconds
                        if ( match.endsWith( 'S' ) ) { 
                            sec = parseInt( match.slice( 0, -1 ) );
                            if ( isNaN( sec ) ) {
                                sec = 0;
                            }
                        }
                    }
                } );

                let time = ( hours * 3600 ) + ( min * 60 ) + sec;
                if ( time < 1 ) {
                    time = 1;
                }

                return time;
            }
        } else {
            let total = 0;

            if ( sessionTime ) {
                const time = Math.trunc( this.formatDuration( sessionTime ) );
                total += time;
            }
            if ( totalTime ) {
                const time = Math.trunc( this.formatDuration( totalTime ) );
                total += time;
            }
        
            return total;    
        }
    
        return undefined;
    }

    formatDuration( time ) {
        const moment = duration( time );
        return Math.floor( moment.asSeconds() );     
    }

    getScore( model ) {
        let rawScore;

        if ( model.cmi?.core?.score ) {
            const score = model.cmi.core.score;
            rawScore = score.raw;
        } else {
            if ( model.cmi?.score ) {
                const score = model.cmi.score;
                rawScore = score.raw;
            }
        }

        if ( rawScore ) {
            const result = parseInt( rawScore, 10 );

            if ( !isNaN( result ) ) {
                this.debug && console.log( 'Score: ', result );
                return result;
            }
        }

        this.debug && console.log( 'Score: NOSCORE' );
        
        return undefined;
    }

    setInitialAssignment() {
        this.debug && console.log( 'Creating initial assisngment' );
        //this.bugsnagNotifyError( 'Setting to assigned' );    

        try {
            apolloClient.mutate( { 
                mutation: PATCH_ASSIGNMENT_MUTATION,
                variables: {
                    input: { 
                        learningObjectID: this.lessonId,
                        courseID: this.courseId,
                        status: ASSIGNED 
                    }
                }
            } );
        }
        catch ( error ) {
            console.error( error ); 
            this.bugsnagNotifyError( 'Failed to set initial assaignment at setInitialAssignment()' );
        }
    }
}
